'use client'

import * as React from 'react'
import { type JSONContent } from '@tiptap/core'
import { Eye, EyeOff, Split, AlignLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { type DiffOperation, type TiptapDiffResult } from '@/utils/tiptap-diff'
import { Button } from '@/components/ui/button'

// Character-level diff utilities for text content
function createCharacterDiff(oldText: string, newText: string) {
  // Simple character-level diff implementation
  const oldChars = oldText.split('')
  const newChars = newText.split('')

  const diff: Array<{
    type: 'equal' | 'insert' | 'delete'
    text: string
  }> = []

  let i = 0,
    j = 0

  while (i < oldChars.length || j < newChars.length) {
    if (
      i < oldChars.length &&
      j < newChars.length &&
      oldChars[i] === newChars[j]
    ) {
      // Equal character
      let equalText = ''
      while (
        i < oldChars.length &&
        j < newChars.length &&
        oldChars[i] === newChars[j]
      ) {
        equalText += oldChars[i]
        i++
        j++
      }
      if (equalText) {
        diff.push({ type: 'equal', text: equalText })
      }
    } else if (
      i < oldChars.length &&
      (j >= newChars.length || oldChars[i] !== newChars[j])
    ) {
      // Deleted character
      let deletedText = ''
      while (
        i < oldChars.length &&
        (j >= newChars.length || oldChars[i] !== newChars[j])
      ) {
        deletedText += oldChars[i]
        i++
        if (j < newChars.length && oldChars[i] === newChars[j]) break
      }
      if (deletedText) {
        diff.push({ type: 'delete', text: deletedText })
      }
    } else if (j < newChars.length) {
      // Inserted character
      let insertedText = ''
      while (
        j < newChars.length &&
        (i >= oldChars.length || oldChars[i] !== newChars[j])
      ) {
        insertedText += newChars[j]
        j++
        if (i < oldChars.length && oldChars[i] === newChars[j]) break
      }
      if (insertedText) {
        diff.push({ type: 'insert', text: insertedText })
      }
    }
  }

  return diff
}

interface EnhancedDiffNodeProps {
  node: JSONContent
  oldNode?: JSONContent
  diffType?: 'added' | 'deleted' | 'modified' | 'unchanged'
  showInlineDiff?: boolean
}

/**
 * Enhanced diff node that renders content similar to TipTap's snapshot comparison
 */
function EnhancedDiffNode({
  node,
  oldNode,
  diffType = 'unchanged',
  showInlineDiff = true,
}: EnhancedDiffNodeProps) {
  const renderTextWithDiff = (text: string, oldText?: string) => {
    if (!showInlineDiff || !oldText || oldText === text) {
      return (
        <span
          className={cn(
            diffType === 'added' && 'rounded bg-green-100 px-1 text-green-900',
            diffType === 'deleted' &&
              'rounded bg-red-100 px-1 text-red-900 line-through',
            diffType === 'modified' && 'rounded bg-blue-100 px-1 text-blue-900'
          )}
        >
          {text}
        </span>
      )
    }

    // Create character-level diff
    const charDiff = createCharacterDiff(oldText, text)

    return (
      <span className='space-x-1'>
        {charDiff.map((chunk, index) => (
          <span
            key={index}
            className={cn(
              chunk.type === 'insert' &&
                'rounded bg-green-200 px-0.5 text-green-900',
              chunk.type === 'delete' &&
                'rounded bg-red-200 px-0.5 text-red-900 line-through',
              chunk.type === 'equal' && ''
            )}
          >
            {chunk.text}
          </span>
        ))}
      </span>
    )
  }

  const renderNodeContent = (): React.ReactNode => {
    switch (node.type) {
      case 'paragraph':
        return (
          <p
            className={cn(
              'prose prose-sm my-2 max-w-none leading-relaxed',
              diffType === 'added' &&
                'border-l-4 border-green-500 bg-green-50/50 pl-4',
              diffType === 'deleted' &&
                'border-l-4 border-red-500 bg-red-50/50 pl-4 opacity-70',
              diffType === 'modified' &&
                'border-l-4 border-blue-500 bg-blue-50/50 pl-4'
            )}
          >
            {node.content?.map((child, index) => (
              <EnhancedDiffNode
                key={index}
                node={child}
                oldNode={oldNode?.content?.[index]}
                diffType={diffType}
                showInlineDiff={showInlineDiff}
              />
            )) || (
              <span className='text-muted-foreground italic'>
                Empty paragraph
              </span>
            )}
          </p>
        )

      case 'text':
        const textContent = node.text || ''
        const oldTextContent = oldNode?.text || ''

        return renderTextWithDiff(textContent, oldTextContent)

      case 'heading':
        const level = node.attrs?.level || 1
        const HeadingComponent =
          level === 1
            ? 'h1'
            : level === 2
              ? 'h2'
              : level === 3
                ? 'h3'
                : level === 4
                  ? 'h4'
                  : level === 5
                    ? 'h5'
                    : 'h6'

        return React.createElement(
          HeadingComponent,
          {
            className: cn(
              'font-semibold leading-tight my-4',
              level === 1 && 'text-2xl',
              level === 2 && 'text-xl',
              level === 3 && 'text-lg',
              level >= 4 && 'text-base',
              diffType === 'added' &&
                'border-l-4 border-green-500 pl-4 bg-green-50/50',
              diffType === 'deleted' &&
                'border-l-4 border-red-500 pl-4 bg-red-50/50 opacity-70',
              diffType === 'modified' &&
                'border-l-4 border-blue-500 pl-4 bg-blue-50/50'
            ),
          },
          node.content?.map((child, index) => (
            <EnhancedDiffNode
              key={index}
              node={child}
              oldNode={oldNode?.content?.[index]}
              diffType={diffType}
              showInlineDiff={showInlineDiff}
            />
          ))
        )

      case 'step':
        return (
          <div
            className={cn(
              'my-4 flex gap-4 rounded-lg border p-4 transition-colors',
              diffType === 'added' && 'border-green-200 bg-green-50/50',
              diffType === 'deleted' &&
                'border-red-200 bg-red-50/50 opacity-70',
              diffType === 'modified' && 'border-blue-200 bg-blue-50/50',
              diffType === 'unchanged' && 'border-border bg-background'
            )}
          >
            {/* Step number indicator */}
            {node.attrs?.number && (
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium',
                  diffType === 'added' &&
                    'border-2 border-green-300 bg-green-100 text-green-800',
                  diffType === 'deleted' &&
                    'border-2 border-red-300 bg-red-100 text-red-800',
                  diffType === 'modified' &&
                    'border-2 border-blue-300 bg-blue-100 text-blue-800',
                  diffType === 'unchanged' && 'bg-muted text-foreground'
                )}
              >
                {node.attrs.number}
              </div>
            )}

            <div className='min-w-0 flex-1'>
              {/* Step title */}
              {node.attrs?.title && (
                <h3 className='mb-2 text-base font-semibold'>
                  {renderTextWithDiff(node.attrs.title, oldNode?.attrs?.title)}
                </h3>
              )}

              {/* Step content */}
              <div className='space-y-2'>
                {node.content?.map((child, index) => (
                  <EnhancedDiffNode
                    key={index}
                    node={child}
                    oldNode={oldNode?.content?.[index]}
                    diffType={diffType}
                    showInlineDiff={showInlineDiff}
                  />
                ))}
              </div>
            </div>
          </div>
        )

      case 'card':
        return (
          <div
            className={cn(
              'my-4 rounded-lg border p-6 transition-colors',
              diffType === 'added' &&
                'border-green-200 bg-green-50/30 shadow-green-100',
              diffType === 'deleted' &&
                'border-red-200 bg-red-50/30 opacity-70 shadow-red-100',
              diffType === 'modified' &&
                'border-blue-200 bg-blue-50/30 shadow-blue-100',
              diffType === 'unchanged' &&
                'border-border bg-background shadow-sm'
            )}
          >
            {/* Card title */}
            {node.attrs?.title && (
              <h3 className='mb-4 text-lg font-semibold'>
                {renderTextWithDiff(node.attrs.title, oldNode?.attrs?.title)}
              </h3>
            )}

            {/* Card content */}
            <div className='space-y-3'>
              {node.content?.map((child, index) => (
                <EnhancedDiffNode
                  key={index}
                  node={child}
                  oldNode={oldNode?.content?.[index]}
                  diffType={diffType}
                  showInlineDiff={showInlineDiff}
                />
              ))}
            </div>
          </div>
        )

      case 'admonition':
        const admonitionType = node.attrs?.type || 'info'
        const typeStyles = {
          info: 'border-blue-200 bg-blue-50/50',
          warning: 'border-yellow-200 bg-yellow-50/50',
          error: 'border-red-200 bg-red-50/50',
          success: 'border-green-200 bg-green-50/50',
        }

        return (
          <div
            className={cn(
              'my-4 rounded-lg border p-4',
              typeStyles[admonitionType as keyof typeof typeStyles],
              diffType === 'added' && 'ring-2 ring-green-200',
              diffType === 'deleted' && 'opacity-70 ring-2 ring-red-200',
              diffType === 'modified' && 'ring-2 ring-blue-200'
            )}
          >
            {/* Admonition title */}
            {node.attrs?.title && (
              <h4 className='mb-2 font-medium'>
                {renderTextWithDiff(node.attrs.title, oldNode?.attrs?.title)}
              </h4>
            )}

            {/* Admonition content */}
            <div className='space-y-2'>
              {node.content?.map((child, index) => (
                <EnhancedDiffNode
                  key={index}
                  node={child}
                  oldNode={oldNode?.content?.[index]}
                  diffType={diffType}
                  showInlineDiff={showInlineDiff}
                />
              ))}
            </div>
          </div>
        )

      default:
        // Handle other node types with generic styling
        return (
          <div
            className={cn(
              'space-y-2 rounded-md border p-3',
              diffType === 'added' && 'border-green-200 bg-green-50/50',
              diffType === 'deleted' &&
                'border-red-200 bg-red-50/50 opacity-70',
              diffType === 'modified' && 'border-blue-200 bg-blue-50/50',
              diffType === 'unchanged' && 'border-border bg-muted/20'
            )}
          >
            <div className='text-muted-foreground text-sm'>
              {node.type} {node.attrs?.title && `- ${node.attrs.title}`}
            </div>
            {node.content && (
              <div className='mt-2 space-y-2'>
                {node.content.map((child, index) => (
                  <EnhancedDiffNode
                    key={index}
                    node={child}
                    oldNode={oldNode?.content?.[index]}
                    diffType={diffType}
                    showInlineDiff={showInlineDiff}
                  />
                ))}
              </div>
            )}
          </div>
        )
    }
  }

  return <>{renderNodeContent()}</>
}

/**
 * Enhanced diff renderer similar to TipTap's snapshot comparison
 */
export function EnhancedDiffRenderer({
  diffResult,
  previous,
  current,
  className,
}: {
  diffResult: TiptapDiffResult
  previous: JSONContent
  current: JSONContent
  className?: string
}) {
  const [viewMode, setViewMode] = React.useState<'unified' | 'split'>('unified')
  const [showInlineDiff, setShowInlineDiff] = React.useState(true)
  const [highlightChanges, setHighlightChanges] = React.useState(true)

  // Create mapping of operations by path for efficient lookup
  const operationMap = React.useMemo(() => {
    const map = new Map<string, DiffOperation>()
    diffResult.operations.forEach((op) => {
      map.set(op.path.join('.'), op)
    })
    return map
  }, [diffResult.operations])

  // Determine diff type for a node based on its path
  const getDiffType = (
    path: string[]
  ): 'added' | 'deleted' | 'modified' | 'unchanged' => {
    if (!highlightChanges) return 'unchanged'

    const pathKey = path.join('.')
    const operation = operationMap.get(pathKey)

    if (!operation) {
      // Check if parent path has operation
      for (let i = path.length - 1; i >= 0; i--) {
        const parentPath = path.slice(0, i).join('.')
        const parentOp = operationMap.get(parentPath)
        if (parentOp) {
          switch (parentOp.type) {
            case 'insert':
              return 'added'
            case 'delete':
              return 'deleted'
            case 'update':
              return 'modified'
          }
        }
      }
      return 'unchanged'
    }

    switch (operation.type) {
      case 'insert':
        return 'added'
      case 'delete':
        return 'deleted'
      case 'update':
        return 'modified'
      default:
        return 'unchanged'
    }
  }

  const renderDocument = (
    doc: JSONContent,
    oldDoc?: JSONContent,
    pathPrefix: string[] = []
  ) => {
    if (!doc.content) return null

    return doc.content.map((node, index) => {
      const nodePath = [...pathPrefix, 'content', index.toString()]
      const diffType = getDiffType(nodePath)
      const oldNode = oldDoc?.content?.[index]

      return (
        <EnhancedDiffNode
          key={`${pathPrefix.join('.')}-${index}`}
          node={node}
          oldNode={oldNode}
          diffType={diffType}
          showInlineDiff={showInlineDiff}
        />
      )
    })
  }

  const renderUnifiedView = () => (
    <div className='space-y-2'>{renderDocument(current, previous, [])}</div>
  )

  const renderSplitView = () => (
    <div className='grid grid-cols-2 gap-6'>
      {/* Previous version */}
      <div className='space-y-4'>
        <div className='text-muted-foreground border-b pb-2 text-sm font-medium'>
          Previous Version
        </div>
        <div className='space-y-1 opacity-75'>
          {renderDocument(previous, undefined, [])}
        </div>
      </div>

      {/* Current version with diff highlighting */}
      <div className='space-y-4'>
        <div className='text-muted-foreground border-b pb-2 text-sm font-medium'>
          Current Version
        </div>
        <div className='space-y-1'>{renderDocument(current, previous, [])}</div>
      </div>
    </div>
  )

  return (
    <div className={cn('space-y-4', className)}>
      {/* Controls */}
      <div className='flex items-center gap-3 border-b pb-3 text-sm'>
        <Button
          variant={viewMode === 'unified' ? 'default' : 'outline'}
          size='sm'
          onClick={() => setViewMode('unified')}
          className='h-8'
        >
          <AlignLeft className='mr-1 h-3 w-3' />
          Unified
        </Button>

        <Button
          variant={viewMode === 'split' ? 'default' : 'outline'}
          size='sm'
          onClick={() => setViewMode('split')}
          className='h-8'
        >
          <Split className='mr-1 h-3 w-3' />
          Split
        </Button>

        <div className='bg-border h-4 w-px' />

        <Button
          variant='outline'
          size='sm'
          onClick={() => setShowInlineDiff(!showInlineDiff)}
          className='h-8'
        >
          {showInlineDiff ? (
            <Eye className='mr-1 h-3 w-3' />
          ) : (
            <EyeOff className='mr-1 h-3 w-3' />
          )}
          Inline Diff
        </Button>

        <Button
          variant='outline'
          size='sm'
          onClick={() => setHighlightChanges(!highlightChanges)}
          className='h-8'
        >
          Highlighting
        </Button>

        <div className='text-muted-foreground ml-auto'>
          {diffResult.stats.added +
            diffResult.stats.modified +
            diffResult.stats.deleted}{' '}
          changes
        </div>
      </div>

      {/* Diff content */}
      <div className='min-h-[200px]'>
        {viewMode === 'split' ? renderSplitView() : renderUnifiedView()}
      </div>

      {/* Empty state */}
      {diffResult.operations.length === 0 && (
        <div className='text-muted-foreground py-12 text-center'>
          <div className='mb-2 text-lg font-medium'>No changes detected</div>
          <div className='text-sm'>The documents are identical</div>
        </div>
      )}
    </div>
  )
}
